import * as Api from '@ViewModels';
import type { IManagedAccountUserReference, IManagedAccountSummary } from '../../queries/ManagedAccount';
import { clearLaunchDarklyStorage } from '../../models/LaunchDarklyStorage';
import {
	clearManagedAccountAccessSession,
	getManagedAccountAccessSession,
	isManagedAccountAccessCredential,
	normalizeManagedAccountAccessCredential,
	setManagedAccountAccessSession,
} from '../../models/ManagedAccountAccessSession';
import type { IManagedAccountAccessAuthToken } from '../../models/ManagedAccountAccessSession';
import { getDisplayName } from '../../models/NameUtils';
import { reactQueryClient } from '../ReactQueryProvider';

/**
 * UI-only gate for surfacing the managed-accounts flow. This is a convenience check to hide the
 * entry points from non-admins; it is NOT an authorization boundary. The backend must independently
 * verify that the caller is an account admin AND that the caller's account manages the requested
 * managed account before issuing an access token.
 */
export function isAccountAdminUser(user?: Api.IUser | null): boolean {
	return Api.isAdmin(user ?? undefined) || user?.groups?.includes('admin') === true;
}

export function getUserReferenceDisplayName(user?: IManagedAccountUserReference | Api.IUser | null): string {
	if (!user) {
		return '';
	}

	const displayName = getDisplayName(user as Api.IPrincipal);
	return displayName || user.email || user.primaryEmail?.value || '';
}

export function reloadToAppPath(path: string) {
	const hashPath = path.startsWith('/') ? path : `/${path}`;
	window.history.replaceState({}, document.title, `${window.location.pathname}#${hashPath}`);
	window.location.reload();
}

export async function switchToManagedAccountAccessSession({
	managedAccount,
	managedUser,
	token,
	userSession,
}: {
	managedAccount: IManagedAccountSummary;
	managedUser: IManagedAccountUserReference;
	token: IManagedAccountAccessAuthToken;
	userSession: Api.UserSessionContext;
}) {
	const credentialStore = userSession.webServiceHelper.getCredentialStore();
	const currentCredential = await credentialStore.getCredential();
	const existingManagedSession = await getManagedAccountAccessSession();
	const parentCredential = isManagedAccountAccessCredential(currentCredential)
		? existingManagedSession?.parentCredential
		: currentCredential;

	if (!parentCredential?.access_token) {
		throw new Error('Unable to preserve your parent account session. Please log in again.');
	}

	const managedCredential = normalizeManagedAccountAccessCredential(token);

	await setManagedAccountAccessSession({
		expiresUtc: managedCredential.expires_utc ?? Date.now(),
		managedAccount: {
			companyName: managedAccount.companyName,
			id: managedAccount.accountId,
		},
		managedUser: {
			email: managedUser.email || managedUser.primaryEmail?.value,
			id: managedUser.id,
			name: getUserReferenceDisplayName(managedUser),
		},
		parentAccount: {
			companyName: userSession.account?.companyName,
			id: userSession.account?.id,
		},
		parentCredential,
		parentUser: {
			email: userSession.user?.email || userSession.user?.primaryEmail?.value,
			id: userSession.user?.id,
			name: getDisplayName(userSession.user),
		},
		startedUtc: Date.now(),
	});

	// The managed-session record is written before the credential swap, so every failure path below
	// must roll the credential AND the record back together — otherwise a mid-switch throw could strand
	// the user with a managed-session record but the parent (or no) credential still active.
	try {
		await credentialStore.setCredential(managedCredential);
		clearLaunchDarklyStorage();
		reactQueryClient.removeQueries();
		await userSession.load();
	} catch (error) {
		await credentialStore.setCredential(parentCredential);
		clearManagedAccountAccessSession();
		clearLaunchDarklyStorage();
		reactQueryClient.removeQueries();
		try {
			// Re-hydrate the parent session so its in-memory state and query cache are consistent again.
			await userSession.load();
		} catch {
			// Parent re-hydration failed too; the parent credential is restored, so surface the
			// original error and let the caller decide how to recover.
		}
		throw error;
	}

	reloadToAppPath('/dashboard');
}

export async function returnToManagementAccountSession(userSession: Api.UserSessionContext) {
	const managedSession = await getManagedAccountAccessSession();
	const parentCredential = managedSession?.parentCredential;

	if (!parentCredential?.access_token) {
		clearManagedAccountAccessSession();
		userSession.logout();
		reactQueryClient.removeQueries();
		reloadToAppPath('/login');
		return;
	}

	await userSession.webServiceHelper.getCredentialStore().setCredential(parentCredential);
	clearManagedAccountAccessSession();
	clearLaunchDarklyStorage();
	reactQueryClient.removeQueries();

	try {
		await userSession.load();
	} catch (error) {
		userSession.logout();
		reloadToAppPath('/login');
		throw error;
	}

	reloadToAppPath('/managed-accounts');
}
